import { useEffect, useMemo, useState } from "react";
import {
  FormCard,
  FormDateField,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  FormSearchSelect,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import { cashLedgerApi, type FinanceSupplier } from "../../api/cashLedgerApi";
import {
  financeStructureApi,
  type FinanceCategory,
  type FinanceFund,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { shekelsToAgorot } from "./cashLedgerUtils";

/** Turbo `PAYMENT_METHODS` — stored as Hebrew labels (same as My-campaigns). */
export const PAYMENT_METHOD_OPTIONS = [
  { value: "", label: "ללא" },
  { value: "מזומן", label: "מזומן" },
  { value: "צ'ק", label: "צ׳ק" },
  { value: "העברה בנקאית", label: "העברה בנקאית" },
  { value: "כרטיס אשראי", label: "כרטיס אשראי" },
  { value: "אחר", label: "אחר" },
] as const;

export const PAYMENT_METHOD_HE: Record<string, string> = Object.fromEntries(
  PAYMENT_METHOD_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

const PAYMENT_STATUS_OPTIONS = [
  { value: "paid", label: "שולם" },
  { value: "unpaid", label: "לא שולם" },
  { value: "partial", label: "שולם חלקי" },
] as const;

type PaymentStatus = (typeof PAYMENT_STATUS_OPTIONS)[number]["value"];

type Option = { id: string; name: string };

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateToIsoDatetime(dateYmd: string): string {
  const d = dateYmd.trim() || todayIsoDate();
  return new Date(`${d}T12:00:00.000Z`).toISOString();
}

function isCheckMethod(method: string): boolean {
  return method === "צ'ק" || method === "צ׳ק";
}

function isBankMethod(method: string): boolean {
  return method === "העברה בנקאית" || isCheckMethod(method);
}

/** Pack Turbo check/bank extras into notes (no dedicated columns in Business yet). */
function appendBankDetails(
  notes: string,
  fields: {
    paymentMethod: string;
    checkNumber: string;
    bankName: string;
    branchNumber: string;
    accountNumber: string;
  },
): string | null {
  const parts: string[] = [];
  if (isCheckMethod(fields.paymentMethod) && fields.checkNumber.trim()) {
    parts.push(`מס׳ צ׳ק: ${fields.checkNumber.trim()}`);
  }
  if (isBankMethod(fields.paymentMethod)) {
    if (fields.bankName.trim()) parts.push(`בנק: ${fields.bankName.trim()}`);
    if (fields.branchNumber.trim()) parts.push(`סניף: ${fields.branchNumber.trim()}`);
    if (fields.accountNumber.trim()) parts.push(`חשבון: ${fields.accountNumber.trim()}`);
  }
  const base = notes.trim();
  if (parts.length === 0) return base || null;
  const block = parts.join(" · ");
  return base ? `${base}\n${block}` : block;
}

function resolveAmountPaid(
  paymentStatus: PaymentStatus,
  amountAgorot: number,
  amountPaidRaw: string,
): number | null {
  if (paymentStatus === "paid") return amountAgorot;
  if (paymentStatus === "unpaid") return 0;
  const paid = shekelsToAgorot(amountPaidRaw);
  if (paid === null) return null;
  if (paid > amountAgorot) return null;
  return paid;
}

function useFinanceLookups(customerId: string, loadSuppliers: boolean) {
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [funds, setFunds] = useState<FinanceFund[]>([]);
  const [suppliers, setSuppliers] = useState<FinanceSupplier[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [cats, fnds] = await Promise.all([
          financeStructureApi.listCustomerCategories(customerId),
          financeStructureApi.listFunds(),
        ]);
        if (cancelled) return;
        setCategories(cats.filter((c) => !c.isSystem));
        setFunds(fnds.filter((f) => f.customerId === customerId));
        if (loadSuppliers) {
          const list = await cashLedgerApi.listSuppliers(customerId);
          if (!cancelled) setSuppliers(list);
        }
      } catch (err) {
        if (!cancelled) setError(toClientApiError(err).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, loadSuppliers]);

  const categoryOptions = useMemo<Option[]>(
    () => categories.map((c) => ({ id: c.id, name: c.displayName ?? c.name })),
    [categories],
  );
  const categoriesById = useMemo(() => {
    const map = new Map<string, FinanceCategory>();
    for (const c of categories) map.set(c.id, c);
    return map;
  }, [categories]);
  const fundOptions = useMemo<Option[]>(
    () => funds.map((f) => ({ id: f.id, name: f.name })),
    [funds],
  );
  const supplierOptions = useMemo<Option[]>(
    () => suppliers.map((s) => ({ id: s.id, name: s.name })),
    [suppliers],
  );
  const defaultFundId = useMemo(
    () => funds.find((f) => f.isDefault)?.id ?? funds[0]?.id ?? null,
    [funds],
  );

  return {
    categoryOptions,
    categoriesById,
    fundOptions,
    supplierOptions,
    defaultFundId,
    error,
  };
}

type BankFields = {
  checkNumber: string;
  setCheckNumber: (v: string) => void;
  bankName: string;
  setBankName: (v: string) => void;
  branchNumber: string;
  setBranchNumber: (v: string) => void;
  accountNumber: string;
  setAccountNumber: (v: string) => void;
};

function PaymentMethodFields({
  paymentMethod,
  setPaymentMethod,
  bank,
}: {
  paymentMethod: string;
  setPaymentMethod: (v: string) => void;
  bank: BankFields;
}) {
  return (
    <>
      <FormField
        label="אמצעי תשלום"
        value={paymentMethod}
        onChange={setPaymentMethod}
        options={[...PAYMENT_METHOD_OPTIONS]}
      />
      {isCheckMethod(paymentMethod) ? (
        <FormField
          label="מספר צ׳ק"
          value={bank.checkNumber}
          onChange={bank.setCheckNumber}
        />
      ) : null}
      {isBankMethod(paymentMethod) ? (
        <>
          <FormField
            label="שם בנק"
            value={bank.bankName}
            onChange={bank.setBankName}
          />
          <FormField
            label="מספר סניף"
            value={bank.branchNumber}
            onChange={bank.setBranchNumber}
          />
          <FormField
            label="מספר חשבון"
            value={bank.accountNumber}
            onChange={bank.setAccountNumber}
          />
        </>
      ) : null}
    </>
  );
}

export type IncomeCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onCreated: () => void;
};

export function IncomeCreateDialog({
  open,
  onClose,
  customerId,
  onCreated,
}: IncomeCreateDialogProps) {
  const lookups = useFinanceLookups(customerId, false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("paid");
  const [amountPaid, setAmountPaid] = useState("");
  const [incomeDate, setIncomeDate] = useState(todayIsoDate);
  const [category, setCategory] = useState<Option | null>(null);
  const [fund, setFund] = useState<Option | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("העברה בנקאית");
  const [checkNumber, setCheckNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchNumber, setBranchNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [reference, setReference] = useState("");
  const [counterpartyName, setCounterpartyName] = useState("");
  const [notes, setNotes] = useState("");
  const [issueViaEzcount, setIssueViaEzcount] = useState(false);
  const [detailedDescription, setDetailedDescription] = useState("");
  const [affectsFund, setAffectsFund] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setAmount("");
    setPaymentStatus("paid");
    setAmountPaid("");
    setIncomeDate(todayIsoDate());
    setCategory(null);
    setPaymentMethod("העברה בנקאית");
    setCheckNumber("");
    setBankName("");
    setBranchNumber("");
    setAccountNumber("");
    setReference("");
    setCounterpartyName("");
    setNotes("");
    setDetailedDescription("");
    setAffectsFund(true);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || fund || !lookups.defaultFundId) return;
    const def = lookups.fundOptions.find((f) => f.id === lookups.defaultFundId);
    if (def) setFund(def);
  }, [open, fund, lookups.defaultFundId, lookups.fundOptions]);

  const save = async () => {
    const amountAgorot = shekelsToAgorot(amount);
    if (!title.trim() || amountAgorot === null) {
      setError("נא למלא כותרת וסכום תקין");
      return;
    }
    const amountPaidAgorot = resolveAmountPaid(
      paymentStatus,
      amountAgorot,
      amountPaid,
    );
    if (amountPaidAgorot === null) {
      setError("נא למלא סכום ששולם חלקי תקין (קטן או שווה לסכום)");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const mergedNotes = appendBankDetails(
        [notes.trim(), detailedDescription.trim()].filter(Boolean).join("\n"),
        {
          paymentMethod,
          checkNumber,
          bankName,
          branchNumber,
          accountNumber,
        },
      );
      await cashLedgerApi.createIncome(customerId, {
        title: title.trim(),
        amountAgorot,
        amountPaidAgorot,
        categoryId: category?.id ?? null,
        fundId: fund?.id ?? null,
        paymentMethod: paymentMethod || null,
        reference: reference.trim() || null,
        counterpartyName: counterpartyName.trim() || null,
        incomeDate: dateToIsoDatetime(incomeDate),
        notes: mergedNotes,
        affectsFund,
        issueViaEzcount,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const bank: BankFields = {
    checkNumber,
    setCheckNumber,
    bankName,
    setBankName,
    branchNumber,
    setBranchNumber,
    accountNumber,
    setAccountNumber,
  };

  return (
    <FormDialog open={open} onClose={busy ? undefined : onClose} size="lg">
      <FormCard
        title="הכנסה חדשה"
        error={error ?? lookups.error}
        onSave={() => void save()}
        onCancel={onClose}
        saveLabel="שמירה"
        cancelLabel="ביטול"
        saving={busy}
        saveDisabled={busy || !title.trim()}
      >
        <FormSection title="פרטי הכנסה">
          <FormGrid>
            <FormField
              label="כותרת / תיאור"
              required
              span={2}
              value={title}
              onChange={setTitle}
              placeholder="למשל: תרומה / החזר / העברה"
            />
            <FormField
              label="סכום (₪)"
              required
              value={amount}
              onChange={setAmount}
              placeholder="0.00"
              inputProps={{ inputMode: "decimal" }}
            />
            <FormDateField
              label="תאריך הכנסה"
              value={incomeDate}
              onChange={setIncomeDate}
            />
            <FormField label="סטטוס תשלום" span={2}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
                {PAYMENT_STATUS_OPTIONS.map((o) => (
                  <label
                    key={o.value}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="radio"
                      name="income-payment-status"
                      checked={paymentStatus === o.value}
                      onChange={() => setPaymentStatus(o.value)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </FormField>
            {paymentStatus === "partial" ? (
              <FormField
                label="סכום ששולם (₪)"
                required
                value={amountPaid}
                onChange={setAmountPaid}
                inputProps={{ inputMode: "decimal" }}
              />
            ) : null}
            <PaymentMethodFields
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              bank={bank}
            />
          </FormGrid>
        </FormSection>

        <FormSection title="סיווג וקרן">
          <FormGrid>
            <FormSearchSelect
              label="קטגוריה"
              options={lookups.categoryOptions}
              value={category}
              onChange={(val: unknown) => {
                const next = val as Option | null;
                setCategory(next);
                if (!next) return;
                const full = lookups.categoriesById.get(next.id);
                if (full?.defaultIncomeAmountAgorot != null) {
                  setAmount(String(full.defaultIncomeAmountAgorot / 100));
                }
              }}
              getOptionLabel={(o) => (o as Option)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as Option | null)?.id === (b as Option | null)?.id
              }
              placeholder="חיפוש קטגוריה…"
            />
            <FormSearchSelect
              label="קרן / קופה פנימית"
              options={lookups.fundOptions}
              value={fund}
              onChange={(val: unknown) => setFund(val as Option | null)}
              getOptionLabel={(o) => (o as Option)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as Option | null)?.id === (b as Option | null)?.id
              }
              placeholder="בחירת קרן…"
            />
            <FormField
              checkbox
              label="משפיע על יתרת קרן"
              checked={affectsFund}
              onChange={
                ((v: boolean) => setAffectsFund(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
          </FormGrid>
        </FormSection>

        <FormSection title="גורם, אסמכתא ופירוט">
          <FormGrid>
            <FormField
              label="שם גורם / תורם"
              value={counterpartyName}
              onChange={setCounterpartyName}
            />
            <FormField
              label="אסמכתא"
              value={reference}
              onChange={setReference}
            />
            <FormField
              label="הערות / פירוט"
              span={2}
              value={notes}
              onChange={setNotes}
              rows={2}
            />
            <FormField
              label="תיאור מפורט"
              span={2}
              value={detailedDescription}
              onChange={setDetailedDescription}
              rows={3}
            />
            <FormField
              checkbox
              label="הנפק קבלה באיזיקאונט"
              checked={issueViaEzcount}
              onChange={
                ((v: boolean) => setIssueViaEzcount(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
          </FormGrid>
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}

export type ExpenseCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onCreated: () => void;
};

export function ExpenseCreateDialog({
  open,
  onClose,
  customerId,
  onCreated,
}: ExpenseCreateDialogProps) {
  const lookups = useFinanceLookups(customerId, true);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("paid");
  const [amountPaid, setAmountPaid] = useState("");
  const [incurredAt, setIncurredAt] = useState(todayIsoDate);
  const [category, setCategory] = useState<Option | null>(null);
  const [fund, setFund] = useState<Option | null>(null);
  const [supplier, setSupplier] = useState<Option | null>(null);
  const [supplierName, setSupplierName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("העברה בנקאית");
  const [checkNumber, setCheckNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchNumber, setBranchNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [detailedDescription, setDetailedDescription] = useState("");
  const [affectsFund, setAffectsFund] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setAmount("");
    setPaymentStatus("paid");
    setAmountPaid("");
    setIncurredAt(todayIsoDate());
    setCategory(null);
    setSupplier(null);
    setSupplierName("");
    setPaymentMethod("העברה בנקאית");
    setCheckNumber("");
    setBankName("");
    setBranchNumber("");
    setAccountNumber("");
    setReference("");
    setNotes("");
    setDetailedDescription("");
    setAffectsFund(true);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || fund || !lookups.defaultFundId) return;
    const def = lookups.fundOptions.find((f) => f.id === lookups.defaultFundId);
    if (def) setFund(def);
  }, [open, fund, lookups.defaultFundId, lookups.fundOptions]);

  const save = async () => {
    const amountAgorot = shekelsToAgorot(amount);
    if (!title.trim() || amountAgorot === null) {
      setError("נא למלא כותרת וסכום תקין");
      return;
    }
    const paidCheck = resolveAmountPaid(paymentStatus, amountAgorot, amountPaid);
    if (paidCheck === null) {
      setError("נא למלא סכום ששולם חלקי תקין (קטן או שווה לסכום)");
      return;
    }
    // Cash expense create currently posts full amount; unpaid/partial stored in notes until schema supports amount_paid UX on create.
    setBusy(true);
    setError(null);
    try {
      const statusNote =
        paymentStatus === "unpaid"
          ? "סטטוס תשלום: לא שולם"
          : paymentStatus === "partial"
            ? `סטטוס תשלום: חלקי (${amountPaid} ₪)`
            : "";
      const supplierFallback =
        !supplier && supplierName.trim()
          ? `ספק: ${supplierName.trim()}`
          : "";
      const refLine = reference.trim() ? `אסמכתא: ${reference.trim()}` : "";
      const mergedNotes = appendBankDetails(
        [notes.trim(), detailedDescription.trim(), statusNote, supplierFallback, refLine]
          .filter(Boolean)
          .join("\n"),
        {
          paymentMethod,
          checkNumber,
          bankName,
          branchNumber,
          accountNumber,
        },
      );
      await cashLedgerApi.createCashExpense(customerId, {
        title: title.trim(),
        amountAgorot,
        categoryId: category?.id ?? null,
        fundId: fund?.id ?? null,
        supplierId: supplier?.id ?? null,
        paymentMethod: paymentMethod || null,
        incurredAt: dateToIsoDatetime(incurredAt),
        notes: mergedNotes,
        affectsFund,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const bank: BankFields = {
    checkNumber,
    setCheckNumber,
    bankName,
    setBankName,
    branchNumber,
    setBranchNumber,
    accountNumber,
    setAccountNumber,
  };

  return (
    <FormDialog open={open} onClose={busy ? undefined : onClose} size="lg">
      <FormCard
        title="הוצאה חדשה"
        error={error ?? lookups.error}
        onSave={() => void save()}
        onCancel={onClose}
        saveLabel="שמירה"
        cancelLabel="ביטול"
        saving={busy}
        saveDisabled={busy || !title.trim()}
      >
        <FormSection title="פרטי הוצאה">
          <FormGrid>
            <FormField
              label="כותרת / תיאור"
              required
              span={2}
              value={title}
              onChange={setTitle}
              placeholder="למשל: שכירות / ספק / רכש"
            />
            <FormField
              label="סכום (₪)"
              required
              value={amount}
              onChange={setAmount}
              placeholder="0.00"
              inputProps={{ inputMode: "decimal" }}
            />
            <FormDateField
              label="תאריך הוצאה"
              value={incurredAt}
              onChange={setIncurredAt}
            />
            <FormField label="סטטוס תשלום" span={2}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
                {PAYMENT_STATUS_OPTIONS.map((o) => (
                  <label
                    key={o.value}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="radio"
                      name="expense-payment-status"
                      checked={paymentStatus === o.value}
                      onChange={() => setPaymentStatus(o.value)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </FormField>
            {paymentStatus === "partial" ? (
              <FormField
                label="סכום ששולם (₪)"
                required
                value={amountPaid}
                onChange={setAmountPaid}
                inputProps={{ inputMode: "decimal" }}
              />
            ) : null}
            <PaymentMethodFields
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              bank={bank}
            />
          </FormGrid>
        </FormSection>

        <FormSection title="סיווג, קרן וספק">
          <FormGrid>
            <FormSearchSelect
              label="קטגוריה"
              options={lookups.categoryOptions}
              value={category}
              onChange={(val: unknown) => {
                const next = val as Option | null;
                setCategory(next);
                if (!next) return;
                const full = lookups.categoriesById.get(next.id);
                if (full?.defaultExpenseAmountAgorot != null) {
                  setAmount(String(full.defaultExpenseAmountAgorot / 100));
                }
              }}
              getOptionLabel={(o) => (o as Option)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as Option | null)?.id === (b as Option | null)?.id
              }
              placeholder="חיפוש קטגוריה…"
            />
            <FormSearchSelect
              label="קרן / קופה פנימית"
              options={lookups.fundOptions}
              value={fund}
              onChange={(val: unknown) => setFund(val as Option | null)}
              getOptionLabel={(o) => (o as Option)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as Option | null)?.id === (b as Option | null)?.id
              }
              placeholder="בחירת קרן…"
            />
            <FormSearchSelect
              label="ספק מהרשימה"
              options={lookups.supplierOptions}
              value={supplier}
              onChange={(val: unknown) => {
                const opt = val as Option | null;
                setSupplier(opt);
                if (opt) setSupplierName(opt.name);
              }}
              getOptionLabel={(o) => (o as Option)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as Option | null)?.id === (b as Option | null)?.id
              }
              placeholder="חיפוש ספק…"
            />
            <FormField
              label="שם ספק (אם אין ברשימה)"
              value={supplierName}
              onChange={setSupplierName}
              disabled={Boolean(supplier)}
            />
            <FormField
              label="אסמכתא"
              value={reference}
              onChange={setReference}
            />
            <FormField
              checkbox
              label="משפיע על יתרת קרן"
              checked={affectsFund}
              onChange={
                ((v: boolean) => setAffectsFund(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
          </FormGrid>
        </FormSection>

        <FormSection title="הערות ופירוט">
          <FormGrid>
            <FormField
              label="הערות / פירוט"
              span={2}
              value={notes}
              onChange={setNotes}
              rows={2}
            />
            <FormField
              label="תיאור מפורט"
              span={2}
              value={detailedDescription}
              onChange={setDetailedDescription}
              rows={3}
            />
          </FormGrid>
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
